/**
 * System Task Schedule Types
 *
 * TypeScript types for scheduled system task execution (artifact cleanup, etc.)
 */

import type { ScheduleType, ScheduleStatus, RecurringInterval, ScheduleConfig } from './workflowSchedule';

// Re-export shared types
export type { ScheduleType, ScheduleStatus, RecurringInterval, ScheduleConfig };

// ============================================================================
// System Task Types
// ============================================================================

export type SystemTaskType = 'artifact_cleanup' | 'memory_cleanup' | 'log_rotation';

export const SYSTEM_TASK_TYPE_LABELS: Record<SystemTaskType, string> = {
  artifact_cleanup: '产物清理',
  memory_cleanup: '内存清理',
  log_rotation: '日志轮转',
};

export const SYSTEM_TASK_TYPE_DESCRIPTIONS: Record<SystemTaskType, string> = {
  artifact_cleanup: '清理过期的用户产物（如幸运符、播客等）',
  memory_cleanup: '清理过期的对话记忆数据',
  log_rotation: '轮转和归档系统日志',
};

// ============================================================================
// Task Config Types
// ============================================================================

export interface ArtifactCleanupConfig {
  artifact_types?: string[]; // Types to clean, empty = all
  delete_files?: boolean; // Whether to delete from storage
  dry_run?: boolean; // Preview without deleting
}

export interface MemoryCleanupConfig {
  older_than_days?: number;
  user_ids?: string[];
}

export interface LogRotationConfig {
  max_age_days?: number;
  compress?: boolean;
}

export type SystemTaskConfig = ArtifactCleanupConfig | MemoryCleanupConfig | LogRotationConfig;

// ============================================================================
// Schedule Models
// ============================================================================

export interface SystemTaskSchedule {
  id: string;
  task_type: SystemTaskType;
  name: string;
  description?: string;
  created_by: string;

  schedule_type: ScheduleType;
  schedule_config: Record<string, unknown>;
  task_config: Record<string, unknown>;

  is_enabled: boolean;
  status: ScheduleStatus;

  next_run_at?: string;
  last_run_at?: string;

  total_runs: number;
  successful_runs: number;
  failed_runs: number;
  last_run_details?: Record<string, unknown>;

  created_at?: string;
  updated_at?: string;
}

// ============================================================================
// API Request/Response
// ============================================================================

export interface SystemTaskScheduleCreateRequest {
  task_type: string;
  name: string;
  description?: string;
  schedule_type: ScheduleType;
  schedule_config: Record<string, unknown>;
  task_config?: Record<string, unknown>;
  is_enabled?: boolean;
}

export interface SystemTaskScheduleUpdateRequest {
  name?: string;
  description?: string;
  schedule_type?: ScheduleType;
  schedule_config?: Record<string, unknown>;
  task_config?: Record<string, unknown>;
  is_enabled?: boolean;
}

export interface SystemTaskScheduleListResponse {
  total: number;
  schedules: SystemTaskSchedule[];
}

export interface SystemTaskScheduleToggleResponse {
  id: string;
  is_enabled: boolean;
  status: ScheduleStatus;
  message: string;
}

export interface SystemTaskScheduleRunResponse {
  schedule_id: string;
  task_type: string;
  success: boolean;
  duration_ms: number;
  details?: Record<string, unknown>;
  errors?: string[];
  message: string;
}

export interface SystemTaskAvailableTypesResponse {
  task_types: Array<{
    type: string;
    name: string;
    description: string;
  }>;
}

// ============================================================================
// Query Parameters
// ============================================================================

export interface SystemTaskScheduleListParams {
  task_type?: string;
  is_enabled?: boolean;
  limit?: number;
  offset?: number;
}

// ============================================================================
// Execution History Types
// ============================================================================

export type SystemTaskExecutionStatus = 'pending' | 'running' | 'completed' | 'failed';

export const EXECUTION_STATUS_LABELS: Record<SystemTaskExecutionStatus, string> = {
  pending: '等待中',
  running: '执行中',
  completed: '已完成',
  failed: '失败',
};

export const EXECUTION_STATUS_COLORS: Record<SystemTaskExecutionStatus, string> = {
  pending: 'bg-gray-100 text-gray-700',
  running: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
};

export interface SystemTaskExecution {
  id: string;
  schedule_id?: string;
  task_type: string;
  task_config: Record<string, unknown>;
  status: SystemTaskExecutionStatus;
  triggered_by: string;
  started_at?: string;
  completed_at?: string;
  duration_ms?: number;
  success?: boolean;
  result_summary?: Record<string, unknown>;
  result_details?: Record<string, unknown>;
  error_message?: string;
  created_at?: string;
}

export interface SystemTaskExecutionListResponse {
  total: number;
  executions: SystemTaskExecution[];
}

export interface SystemTaskExecutionListParams {
  schedule_id?: string;
  task_type?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

// ============================================================================
// UI Helper Types
// ============================================================================

export interface SystemTaskScheduleFormData {
  name: string;
  description: string;
  task_type: SystemTaskType | '';
  schedule_type: ScheduleType;

  // Once
  run_at: Date | null;

  // Recurring
  interval: RecurringInterval;
  time: string;
  timezone: string;
  days_of_week: number[];
  days_of_month: number[];

  // Cron
  cron_expression: string;

  // Task config (JSON string for flexibility)
  task_config: string;

  is_enabled: boolean;
}

export const DEFAULT_SYSTEM_TASK_SCHEDULE_FORM: SystemTaskScheduleFormData = {
  name: '',
  description: '',
  task_type: '',
  schedule_type: 'cron',
  run_at: null,
  interval: 'daily',
  time: '03:00', // Default to 3am for cleanup tasks
  timezone: 'Asia/Shanghai',
  days_of_week: [0, 1, 2, 3, 4, 5, 6], // Every day
  days_of_month: [1],
  cron_expression: '0 3 * * *', // Daily at 3am
  task_config: '{}',
  is_enabled: true,
};

// ============================================================================
// Schedule Category (Shared)
// ============================================================================

export type ScheduleCategory = 'workflow' | 'system_task';

export const SCHEDULE_CATEGORY_LABELS: Record<ScheduleCategory, string> = {
  workflow: 'Workflow 定时任务',
  system_task: '系统定时任务',
};

export const SCHEDULE_CATEGORY_DESCRIPTIONS: Record<ScheduleCategory, string> = {
  workflow: '定时执行图像工作流，如每日生成幸运符',
  system_task: '定时执行系统维护任务，如清理过期产物',
};
